import { VehicleModel, VehiclePowertrain, VehicleTag, Manufacturer } from '@/types';
import {
  VEHICLE_TYPE_OPTIONS,
  POWERTRAIN_OPTIONS,
  VEHICLE_TAG_OPTIONS,
  DATA_SOURCE_LABELS,
} from '@/constants/vehicleModelConstants';
import { VehicleModelSortKey, VehicleModelSortOrder } from '@/hooks/useVehicleModelFilters';

// ラベル変換ユーティリティ
export const getVehicleTypeLabel = (vehicleTypeId: string): string => {
  const option = VEHICLE_TYPE_OPTIONS.find(opt => opt.value === vehicleTypeId);
  return option ? option.label : vehicleTypeId;
};

export const getPowertrainLabel = (powertrain: VehiclePowertrain | null): string => {
  if (!powertrain) return '';
  const option = POWERTRAIN_OPTIONS.find(opt => opt.value === powertrain);
  return option ? option.label : powertrain;
};

export const getPowertrainColorClasses = (powertrain: VehiclePowertrain | null): string => {
  if (!powertrain) return 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300';

  switch (powertrain) {
    case VehiclePowertrain.GASOLINE:
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
    case VehiclePowertrain.DIESEL:
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300';
    case VehiclePowertrain.HYBRID:
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
    case VehiclePowertrain.SERIES_HYBRID:
      return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300';
    case VehiclePowertrain.MILD_HYBRID:
      return 'bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300';
    case VehiclePowertrain.PLUGIN_HYBRID:
      return 'bg-lime-100 dark:bg-lime-900/30 text-lime-800 dark:text-lime-300';
    case VehiclePowertrain.ELECTRIC:
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
    default:
      return 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300';
  }
};

export const getVehicleTagLabels = (tags: VehicleTag[]): string[] => {
  return tags.map(tag => {
    const option = VEHICLE_TAG_OPTIONS.find(opt => opt.value === tag);
    return option ? option.label : tag;
  });
};

export const getDataSourceLabel = (dataSource: string): string => {
  return dataSource === 'rakuhai_provided'
    ? DATA_SOURCE_LABELS.RAKUHAI_PROVIDED
    : DATA_SOURCE_LABELS.CUSTOM;
};

// フィルタリング関数
export const filterVehicleModels = (
  vehicleModels: VehicleModel[],
  filters: {
    searchQuery: string;
    selectedManufacturer: string;
    selectedVehicleType: string;
    selectedPowertrain: VehiclePowertrain | '';
    selectedTags: VehicleTag[];
  }
): VehicleModel[] => {
  return vehicleModels.filter(model => {
    // テキスト検索
    if (filters.searchQuery) {
      const searchLower = filters.searchQuery.toLowerCase();
      const matchesSearch =
        model.name.toLowerCase().includes(searchLower) ||
        `${model.manufacturerId} ${model.name}`.toLowerCase().includes(searchLower);

      if (!matchesSearch) return false;
    }

    // メーカーフィルター
    if (filters.selectedManufacturer && model.manufacturerId !== filters.selectedManufacturer) {
      return false;
    }

    // 車種タイプフィルター
    if (filters.selectedVehicleType && model.vehicleTypeId !== filters.selectedVehicleType) {
      return false;
    }

    // パワートレインフィルター
    if (filters.selectedPowertrain) {
      if (model.powertrain !== filters.selectedPowertrain) {
        return false;
      }
    }

    // タグフィルター
    if (filters.selectedTags.length > 0) {
      const hasAllTags = filters.selectedTags.every(
        tag => model.vehicleTags && model.vehicleTags.includes(tag)
      );
      if (!hasAllTags) return false;
    }

    return true;
  });
};

// データ変換ユーティリティ
export const getUniqueManufacturers = (vehicleModels: VehicleModel[]): string[] => {
  const manufacturers = vehicleModels.map(model => model.manufacturerId);
  return Array.from(new Set(manufacturers)).sort();
};

// バリデーション関数
export const validateVehicleModel = (model: Partial<VehicleModel>): string[] => {
  const errors: string[] = [];

  if (!model.manufacturerId?.trim()) {
    errors.push('メーカーは必須です');
  }

  if (!model.name?.trim()) {
    errors.push('モデルは必須です');
  }

  if (!model.vehicleTypeId) {
    errors.push('車種タイプは必須です');
  }

  return errors;
};

// ソート関数
export const sortVehicleModels = (
  vehicleModels: VehicleModel[],
  manufacturers: Manufacturer[],
  sortKey: VehicleModelSortKey,
  sortOrder: VehicleModelSortOrder = 'asc'
): VehicleModel[] => {
  return [...vehicleModels].sort((a, b) => {
    let comparison = 0;

    switch (sortKey) {
      case 'manufacturer': {
        const manufacturerA =
          manufacturers.find(m => m.id === a.manufacturerId)?.name || a.manufacturerId;
        const manufacturerB =
          manufacturers.find(m => m.id === b.manufacturerId)?.name || b.manufacturerId;
        comparison = manufacturerA.localeCompare(manufacturerB, 'ja');
        break;
      }
      case 'modelName':
        comparison = a.name.localeCompare(b.name, 'ja');
        break;
      case 'createdAt': {
        // createdAtフィールドがない場合はidで代替（UUIDの生成順序に依存）
        const dateA = 'createdAt' in a ? new Date(a.createdAt as string).getTime() : 0;
        const dateB = 'createdAt' in b ? new Date(b.createdAt as string).getTime() : 0;
        comparison = dateA - dateB;
        break;
      }
      default:
        comparison = 0;
        break;
    }

    return sortOrder === 'desc' ? -comparison : comparison;
  });
};
